import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReactiveFormsModule } from '@angular/forms';

import { AisSharedModule } from '../shared/ais-shared.module';
import {
  TrackClusterControlComponent,
  TrackCloseControlComponent,
  TrackDimControlComponent,
  TrackEntryControlComponent,
  TrackOptionsPanelComponent,
  TrackSourceControlComponent,
  TrackTitleControlComponent,
} from './toolbar/controls/toolbar-track-controls';
import { VesselHistoryControlComponent } from './toolbar/controls/toolbar-vessel-controls';
import {
  PortCallControlComponent,
  PortCallsPanelComponent,
} from './toolbar/controls/toolbar-port-call-controls';
import {
  NearbyControlComponent,
  NearbyPanelComponent,
} from './toolbar/controls/toolbar-nearby-controls';
import {
  SearchControlComponent,
  SearchPanelComponent,
} from './toolbar/controls/toolbar-search-controls';
import { VoyageRefreshButtonComponent } from './panels/sensor-row/voyage-refresh-button.component';
import { VoyageRefreshHistoryComponent } from './panels/refresh-history/voyage-refresh-history.component';
import { VoyageTrackHistoryComponent } from './panels/track-history/voyage-track-history.component';
import { VoyageVesselHistoryComponent } from './panels/vessel-history/voyage-vessel-history.component';
import { VoyageVesselTypesComponent } from './panels/vessel-types/voyage-vessel-types.component';
import { VoyageHistoryBrowserComponent } from './panels/history-browser/voyage-history-browser.component';
import { VoyageCreditRefundComponent } from './panels/credit-refund/voyage-credit-refund.component';
import { VR_TOOLBAR_CONTROL_PROVIDERS } from './toolbar/vr-toolbar-controls.providers';
import { VR_TOOLBAR_CONFIG_PROVIDERS } from './toolbar/vr-toolbar.config';

/**
 * VOYAGE RADAR — everything this provider contributes to the map (D16).
 *
 * Its ten toolbar controls and their drawers, its seven sidebar panels and
 * dialogs, its control descriptors and its bar composition. One import in
 * `app.module.ts`.
 *
 * THE SHAPE THIS COMPLETES. D9 gave a provider its own control registry, D13
 * its own bar configuration, D15 its own card actions — each ending with "and
 * it edits nothing shared". Each was true inside `ais/` and false of
 * `app.module.ts`, which still needed the imports, the declarations and the
 * providers. This is the last of it: adding a sensor means adding a module.
 *
 * `AisSharedModule` is imported for the drawer and the type-filter list its
 * controls render into — the only two shared components that cross this
 * boundary — and NOT re-exported. A consumer wanting the shared chrome imports
 * it directly; getting it transitively through a vendor would make the vendor
 * look like a source of things it does not own.
 */
const DECLARATIONS = [
  // The four lookup drawers: search, port calls, nearby, and the track entry.
  // Each is a control (the button) plus a panel (the drawer body) — the panel
  // is declared and never exported, because only its control renders it.
  SearchControlComponent,
  SearchPanelComponent,
  PortCallControlComponent,
  PortCallsPanelComponent,
  NearbyControlComponent,
  NearbyPanelComponent,
  VesselHistoryControlComponent,
  // The per-vessel track bar.
  TrackCloseControlComponent,
  TrackTitleControlComponent,
  TrackClusterControlComponent,
  TrackDimControlComponent,
  TrackSourceControlComponent,
  TrackEntryControlComponent,
  TrackOptionsPanelComponent,
  // Sidebar panels, mounted by selector from the map component's template.
  VoyageRefreshButtonComponent,
  VoyageRefreshHistoryComponent,
  VoyageTrackHistoryComponent,
  VoyageVesselHistoryComponent,
  VoyageVesselTypesComponent,
  // Dialogs — opened by class, never by selector.
  VoyageHistoryBrowserComponent,
  VoyageCreditRefundComponent,
];

@NgModule({
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    // `formGroup` / `formControlName` / `[maxlength]` in the credit-refund
    // dialog — the only reactive form in the AIS tree. Found by AOT (NG8002)
    // rather than by reading, which is the reason a module is safer here than
    // a barrel of arrays: a missing import is a compile error.
    ReactiveFormsModule,
    AisSharedModule,
  ],
  declarations: DECLARATIONS,
  exports: [
    // Only what the map component's own template mounts. The toolbar controls
    // are built by `NgComponentOutlet` from the registry and must not be
    // reachable by selector — that is what keeps the registry the one way in.
    VoyageRefreshButtonComponent,
    VoyageRefreshHistoryComponent,
    VoyageTrackHistoryComponent,
    VoyageVesselHistoryComponent,
    VoyageVesselTypesComponent,
  ],
  providers: [
    // Two `multi` tokens, contributed into the same arrays the shared module
    // and every other provider feed: what controls exist, and which bars this
    // operator shows.
    ...VR_TOOLBAR_CONTROL_PROVIDERS,
    ...VR_TOOLBAR_CONFIG_PROVIDERS,
  ],
})
export class VoyageRadarModule {}
